import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';
import { TokenService } from '../services/token.service';

/**
 * Header interno para marcar una request que ya fue reintentada por 401.
 * Evita loops infinitos si el nuevo token también falla.
 */
const RETRY_HEADER = 'X-Auth-Retry';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);

  // No adjuntar Bearer al propio endpoint de token (evita circularidad).
  const isTokenEndpoint = req.url.includes('/oauth/token');
  const alreadyRetried = req.headers.has(RETRY_HEADER);

  const attachBearer = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
    const token = tokenService.getUserToken() ?? tokenService.getClientToken();
    if (!token) return request;
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  };

  const outgoing = isTokenEndpoint ? req : attachBearer(req);

  return next(outgoing).pipe(
    catchError((err) => {
      // Solo intervenimos en 401 para requests no-token, no-retried.
      if (
        !(err instanceof HttpErrorResponse) ||
        err.status !== 401 ||
        isTokenEndpoint ||
        alreadyRetried
      ) {
        return throwError(() => err);
      }

      // Si había token de usuario, no reintentamos con client_credentials:
      // un 401 de sesión de usuario debe llevar al flujo de login.
      const hadUserToken = !!tokenService.getUserToken();
      if (hadUserToken) {
        return throwError(() => err);
      }

      if (!environment.production) {
        console.warn('[auth] 401 con token cliente — renegociando', {
          url: req.url,
        });
      }

      // Purgar el token cliente que falló y asegurar uno válido, luego
      // reintentar una vez. Solo se purga si la petición llevaba Bearer: un 401
      // sin token no dice nada del guardado. En SSR el token es de todo el
      // proceso, así que se purga solo si sigue siendo el que falló (otra
      // request pudo renovarlo ya) y `ensure` lo reutiliza sin POST si es bueno.
      const attached = outgoing.headers.get('Authorization')?.replace(/^Bearer\s+/, '');
      if (attached) {
        tokenService.clearClientToken(attached);
      }
      return authService.ensureClientCredentials().pipe(
        switchMap(() => {
          const fresh = tokenService.getClientToken();
          if (!fresh) return throwError(() => err);
          const retried = req.clone({
            setHeaders: {
              Authorization: `Bearer ${fresh}`,
              [RETRY_HEADER]: '1',
            },
          });
          return next(retried);
        }),
      );
    }),
  );
};
